import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import NextLink from 'next/link';

import Anchor from '../../atoms/anchor';
import { getHref } from '../../utils/navigation';
import Link from '../../utils/link';
import trackEvent from '../../utils/tracking';
import Typography from '../../atoms/typography';
import Picture from '../../atoms/cms-picture';
import IconCircleArrow from '../../atoms/icon-circle-arrow';
import Ruler from '../../atoms/ruler';

const Wrapper = styled.article`
  align-items: center;
  display: flex;
  flex-direction: column;
  max-width: 200px;
  margin: auto;
  padding-bottom: 3rem;

  @media screen and (min-width: ${(props) => props.theme.breakpoint.small}) {
    max-width: 100%;
    padding-bottom: 0;
  }
`;

const Title = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
  margin-bottom: ${(props) => props.theme.modularScale.base};
  text-align: center;
`;

const Description = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
  margin-bottom: ${(props) => props.theme.modularScale.base};
  text-align: center;
`;

const Image = styled(Picture)`
  margin-bottom: ${(props) => props.theme.modularScale.base};
`;

const CircleBannerLink = styled.a`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;

  svg {
    height: 2.7rem;
    margin-bottom: ${(props) => props.theme.modularScale.small};
    width: 2.7rem;
    circle,
    path {
      fill: none;
      stroke: ${(props) => props.theme.color.brand};
      stroke-width: 4;
    }
  }

  :focus,
  :hover:not(:disabled) {
    svg {
      path {
        animation: arrow-head;
        animation-duration: ${(props) => props.theme.animation.slow};
        animation-timing-function: ${(props) => props.theme.animation.easeMove};
        animation-delay: 0s;
        animation-iteration-count: 1;
        animation-direction: normal;
        animation-fill-mode: forwards;
        animation-play-state: running;
      }
    }
  }

  span {
    color: ${(props) => props.theme.color.brand};
    font-family: ${(props) => props.theme.font.caption};
    font-size: ${(props) => props.theme.modularScale.small};
    text-transform: uppercase;
    letter-spacing: .24rem;
    margin-top: 1rem;
  }
`;

const StyledAnchor = styled(Anchor)`
  text-align: center;
  ${Title}, ${Description}, ${CircleBannerLink} > span {
    transition: color ${(props) => props.theme.animation.fast} ${(props) => props.theme.animation.easeOutQuad};
  }

  :hover {
    ${Title}, ${Description}, ${CircleBannerLink} > span {
      color: ${({ theme }) => theme.color.brandLight};
    }
  }

  :focus,
  :hover:not(:disabled) {
    ${CircleBannerLink} svg {
      path {
        animation: arrow-head;
        animation-duration: ${(props) => props.theme.animation.slow};
        animation-timing-function: ${(props) => props.theme.animation.easeMove};
        animation-delay: 0s;
        animation-iteration-count: 1;
        animation-direction: normal;
        animation-fill-mode: forwards;
        animation-play-state: running;
      }
    }
  }
`;

const handleTracking = () => trackEvent({
  eventAction: 'Homepage',
  eventCategory: 'Content Engagement',
  eventLabel: 'Content Slot 2 (4 Circles)'
});

const CircleBanner = (props) => (
  <>
    <Wrapper onClick={handleTracking}>
      <NextLink
        href={getHref({ navigation_url: props.data.url })}
        passHref
      >
        <StyledAnchor>
          {props.data?.image
          && (
            <Image
              {...props.data.image}
              circle
              alt={props.data.image_alt}
            />
          )}
          {props.data?.title
          && <Title element="h2" like="heading-5">{props.data.title}</Title>}
          {props.data?.description
          && <Description element="p" like="paragraph-2">{props.data.description}</Description>}
          {props.data?.url
          && (
            <Link
              href={getHref({ navigation_url: props.data.url })}
              passHref
            >
              <CircleBannerLink>
                <IconCircleArrow />
                <span>{props.data?.link_text ?? 'Shop Now'}</span>
              </CircleBannerLink>
            </Link>
          )}
        </StyledAnchor>
      </NextLink>
    </Wrapper>

    {props.data?.hr && <Ruler />}
  </>
);

CircleBanner.propTypes = {
  data: PropTypes.object.isRequired
};

CircleBanner.whyDidYouRender = true;

export default CircleBanner;
